import { useEffect } from "react";
import {
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import GradingIcon from "@mui/icons-material/Grading";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  selectCurrentCanvasContributors,
  selectGradingViewUserId,
  selectGradingViewUsername,
  selectIsCurrentUserWorkspaceOwner,
  selectIsEvaluator,
  selectIsGradingViewActive,
  selectSelectedCanvasId,
} from "@store/selectors";
import {
  enterGradingView,
  exitGradingView,
  loadCanvasContributors,
} from "@store/thunks/workspaceThunks";
import { selectSidebarMode } from "@store/slices/viewSlice";

/**
 * Stage 6.8: canvas counterpart of GradingViewBar. The picker
 * selection is workspace-level so flipping the reader/canvas toggle
 * preserves the chosen contributor.
 */
const CanvasGradingViewBar = ({ inline = false }: { inline?: boolean }) => {
  const dispatch = useAppDispatch();
  const isOwner = useAppSelector(selectIsCurrentUserWorkspaceOwner);
  const isEvaluator = useAppSelector(selectIsEvaluator);
  const selectedCanvasId = useAppSelector(selectSelectedCanvasId);
  const contributors = useAppSelector(selectCurrentCanvasContributors);
  const isActive = useAppSelector(selectIsGradingViewActive);
  const currentUserId = useAppSelector(selectGradingViewUserId);
  const currentUsername = useAppSelector(selectGradingViewUsername);
  const sidebarMode = useAppSelector(selectSidebarMode);

  useEffect(() => {
    if (isOwner && isEvaluator && selectedCanvasId) {
      dispatch(loadCanvasContributors());
    }
  }, [dispatch, isOwner, isEvaluator, selectedCanvasId]);

  if (!isOwner || !isEvaluator || !selectedCanvasId) return null;
  if (!isActive && sidebarMode !== "evaluate") return null;

  const currentIndex = currentUserId
    ? contributors.findIndex((c) => c.user_id === currentUserId)
    : -1;
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex >= 0 && currentIndex < contributors.length - 1;

  const handlePickContributor = (userId: string) => {
    const contributor = contributors.find((c) => c.user_id === userId);
    if (!contributor) return;
    dispatch(
      enterGradingView({ userId: contributor.user_id, username: contributor.username })
    );
  };

  const handleExit = () => {
    dispatch(exitGradingView());
  };

  const handlePrev = () => {
    if (!canGoPrev) return;
    const prev = contributors[currentIndex - 1];
    if (!prev) return;
    dispatch(enterGradingView({ userId: prev.user_id, username: prev.username }));
  };

  const handleNext = () => {
    if (!canGoNext) return;
    const next = contributors[currentIndex + 1];
    if (!next) return;
    dispatch(enterGradingView({ userId: next.user_id, username: next.username }));
  };

  return (
    <Paper
      elevation={inline ? 0 : 2}
      sx={{
        ...(inline
          ? { position: "static", width: "100%", mb: 2, flexWrap: "wrap" }
          : { position: "absolute", top: 76, left: 16, zIndex: 10 }),
        px: 1.5,
        py: 0.75,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        border: 1,
        borderColor: "divider",
        borderLeftWidth: isActive ? 4 : 1,
        borderLeftColor: isActive ? "secondary.main" : "divider",
      }}
    >
      <GradingIcon fontSize="small" color={isActive ? "secondary" : "action"} />

      {!isActive ? (
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="canvas-grading-view-picker-label">
            Grade contributor
          </InputLabel>
          <Select
            labelId="canvas-grading-view-picker-label"
            label="Grade contributor"
            value=""
            onChange={(e) => handlePickContributor(e.target.value as string)}
            disabled={contributors.length === 0}
          >
            {contributors.length === 0 ? (
              <MenuItem value="" disabled>
                No contributors yet
              </MenuItem>
            ) : (
              contributors.map((c) => (
                <MenuItem key={c.user_id} value={c.user_id}>
                  {c.username}
                  <Chip
                    label={`${c.rectangle_count} rect`}
                    size="small"
                    sx={{ ml: 1 }}
                    variant="outlined"
                  />
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      ) : (
        <>
          <Tooltip title="Previous contributor">
            <span>
              <IconButton
                size="small"
                onClick={handlePrev}
                disabled={!canGoPrev}
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={currentUserId ?? ""}
              onChange={(e) => handlePickContributor(e.target.value as string)}
            >
              {contributors.map((c) => (
                <MenuItem key={c.user_id} value={c.user_id}>
                  {c.username}
                  <Chip
                    label={`${c.rectangle_count} rect`}
                    size="small"
                    sx={{ ml: 1 }}
                    variant="outlined"
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Next contributor">
            <span>
              <IconButton
                size="small"
                onClick={handleNext}
                disabled={!canGoNext}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            Viewing {currentUsername} · read-only
          </Typography>
          <Tooltip title="Exit grading view">
            <Button
              size="small"
              variant="text"
              onClick={handleExit}
              startIcon={<CloseIcon fontSize="small" />}
              sx={{ ml: 0.5 }}
            >
              Exit
            </Button>
          </Tooltip>
        </>
      )}
    </Paper>
  );
};

export default CanvasGradingViewBar;
